import { useState, useEffect, useMemo } from 'react';
import { BACKEND_URL } from '../config';

interface PlayerAvatarProps {
  pseudo: string;
  size?: number;
  className?: string;
  photoUrl?: string | null; // URL directe de la photo (évite le fetch /info)
  cacheBust?: number; // Force le rechargement après changement d'avatar
}

// Couleurs de fond pour les avatars par défaut (basées sur le hash du pseudo)
const AVATAR_COLORS = [
  '#e91e63', // Rose
  '#9c27b0', // Violet
  '#673ab7', // Violet profond
  '#3f51b5', // Indigo
  '#2196f3', // Bleu
  '#03a9f4', // Bleu clair
  '#00bcd4', // Cyan
  '#009688', // Teal
  '#4caf50', // Vert
  '#8bc34a', // Vert clair
  '#ff9800', // Orange
  '#ff5722', // Orange foncé
  '#795548', // Marron
  '#607d8b', // Gris bleu
];

// Obtenir une couleur basée sur le pseudo
function getColorForPseudo(pseudo: string): string {
  let hash = 0;
  for (let i = 0; i < pseudo.length; i++) {
    hash = pseudo.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

// Obtenir les initiales du pseudo (1-2 caractères)
function getInitials(pseudo: string): string {
  const cleaned = pseudo.replace(/[^a-zA-Z0-9]/g, '');
  if (cleaned.length === 0) return '?';
  if (cleaned.length === 1) return cleaned.toUpperCase();
  
  // Pour les pseudos avec majuscules internes (ex: "JeanPierre")
  const capitals = cleaned.match(/[A-Z]/g);
  if (capitals && capitals.length >= 2) {
    return (capitals[0] + capitals[1]).toUpperCase();
  }
  
  // Sinon, prendre les 2 premières lettres
  return cleaned.substring(0, 2).toUpperCase();
}

// Cache global pour savoir quels pseudos ont une photo
const photoCache = new Map<string, boolean | null>(); // null = en cours de chargement

export function PlayerAvatar({ pseudo, size = 48, className = '', photoUrl, cacheBust }: PlayerAvatarProps) {
  const [hasPhoto, setHasPhoto] = useState<boolean | null>(photoUrl ? true : null);
  const [imageError, setImageError] = useState(false);

  // Couleur et initiales calculées une seule fois
  const backgroundColor = useMemo(() => getColorForPseudo(pseudo), [pseudo]);
  const initials = useMemo(() => getInitials(pseudo), [pseudo]);

  // URL de l'avatar (utiliser photoUrl directe si disponible, sinon via notre proxy)
  // Demander 2x la taille pour les écrans retina
  const retinaSize = size * 2;
  const cacheSuffix = cacheBust ? `&t=${cacheBust}` : '';
  const avatarUrl = photoUrl || `${BACKEND_URL}/api/avatars/${encodeURIComponent(pseudo)}?size=${retinaSize}${cacheSuffix}`;

  useEffect(() => {
    // Si on a déjà une photoUrl directe, pas besoin de fetch
    if (photoUrl) {
      setHasPhoto(true);
      setImageError(false);
      return;
    }

    // Réinitialiser l'état d'erreur si le pseudo ou la version cache change
    setImageError(false);

    // Vérifier le cache
    const cached = photoCache.get(pseudo.toLowerCase());
    if (cached !== undefined) {
      setHasPhoto(cached);
      return;
    }

    // Marquer comme en cours de chargement
    photoCache.set(pseudo.toLowerCase(), null);

    // Vérifier si le joueur a une photo
    fetch(`${BACKEND_URL}/api/avatars/${encodeURIComponent(pseudo)}/info`)
      .then(res => res.json())
      .then(data => {
        const has = data.hasPhoto === true;
        photoCache.set(pseudo.toLowerCase(), has);
        setHasPhoto(has);
      })
      .catch(() => {
        photoCache.set(pseudo.toLowerCase(), false);
        setHasPhoto(false);
      });
  }, [pseudo, photoUrl, cacheBust]);

  // Calculer la taille de la police pour les initiales
  const fontSize = Math.floor(size * 0.4);

  // Style commun pour le conteneur
  const containerStyle: React.CSSProperties = {
    width: size,
    height: size,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    overflow: 'hidden',
  };

  // Si on a une photo et pas d'erreur de chargement
  if (hasPhoto && !imageError) {
    return (
      <div 
        className={`player-avatar ${className}`}
        style={{
          ...containerStyle,
          backgroundColor, // Fallback pendant le chargement
        }}
      >
        <img
          src={avatarUrl}
          alt={pseudo}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
          onError={() => setImageError(true)}
          loading="lazy"
        />
      </div>
    );
  }

  // Avatar par défaut avec initiales
  return (
    <div
      className={`player-avatar player-avatar-initials ${className}`}
      style={{
        ...containerStyle,
        backgroundColor,
        color: 'white',
        fontSize: `${fontSize}px`,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '-1px',
      }}
      title={pseudo}
    >
      {initials}
    </div>
  );
}

// Hook pour précharger les infos d'avatar de plusieurs joueurs
export function usePrefetchAvatars(pseudos: string[]) {
  useEffect(() => {
    if (pseudos.length === 0) return;

    // Filtrer les pseudos non encore en cache
    const toFetch = pseudos.filter(p => photoCache.get(p.toLowerCase()) === undefined);
    if (toFetch.length === 0) return;

    // Batch request
    fetch(`${BACKEND_URL}/api/avatars/batch`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pseudos: toFetch }),
    })
      .then(res => res.json())
      .then(data => {
        if (data.items) {
          for (const item of data.items) {
            photoCache.set(item.pseudo.toLowerCase(), item.hasPhoto);
          }
        }
      })
      .catch(console.error);
  }, [pseudos.join(',')]);
}

// Fonction utilitaire pour invalider le cache d'un pseudo
export function invalidateAvatarCache(pseudo: string) {
  photoCache.delete(pseudo.toLowerCase());
}

// Fonction utilitaire pour invalider tout le cache
export function clearAvatarCache() {
  photoCache.clear();
}

export default PlayerAvatar;
